import readImageDimensions, { Dimensions } from "./readImageDimensions";

// Dimensions are memoized per File object so repeated validations
// (e.g. after adding/removing files) never re-read the same image.
const dimensionsCache = new WeakMap<File, Dimensions>();

// Max number of files read in parallel. Reading is cheap (header only), but we
// still bound concurrency to avoid spawning hundreds of FileReaders at once.
const MAX_CONCURRENT_DECODES = 8;

const getImageDimensions = async (file: File): Promise<Dimensions> => {
  const cached = dimensionsCache.get(file);
  if (cached) {
    return cached;
  }

  const dimensions = await readImageDimensions(file);
  dimensionsCache.set(file, dimensions);
  return dimensions;
};

async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await fn(items[index], index);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => worker())
  );

  return results;
}

/**
 * Find files that do not have the most frequent dimensions.
 * @param fileList List of files
 * @returns list of file names with different dimensions
 */
async function findFilesWithDifferentDimensions(fileList: File[]) {
  if (fileList.length <= 1) {
    return [];
  }

  const entries = await mapWithConcurrency(
    fileList,
    MAX_CONCURRENT_DECODES,
    async (file) => ({
      name: file.name,
      dimensions: await getImageDimensions(file),
    })
  );

  const dimensionCounts = new Map<string, number>();
  for (const entry of entries) {
    const dimensionKey = `${entry.dimensions.width}x${entry.dimensions.height}`;
    dimensionCounts.set(dimensionKey, (dimensionCounts.get(dimensionKey) ?? 0) + 1);
  }

  let referenceDimensionKey = "";
  let maxCount = 0;
  for (const [dimensionKey, count] of dimensionCounts.entries()) {
    if (count > maxCount) {
      maxCount = count;
      referenceDimensionKey = dimensionKey;
    }
  }

  const filesWithDifferentDimensions = new Set<string>();

  for (const entry of entries) {
    const currentDimensionKey = `${entry.dimensions.width}x${entry.dimensions.height}`;
    if (currentDimensionKey !== referenceDimensionKey) {
      filesWithDifferentDimensions.add(entry.name);
    }
  }

  return Array.from(filesWithDifferentDimensions);
}

export default findFilesWithDifferentDimensions;
